import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import StepHeader from "./StepHeader";
import ScanCentreCard from "./ScanCentreCard";
import ClinicDetailsModal from "./ClinicDetailsModal";
import { StripeProduct } from '../../api/subscription';
import { searchScanCenters, ScanCenter } from '../../api/scans';
import { toast } from 'react-toastify';
import LocationIcon from '../../assets/icons/fbs/LocationIcon.svg';

interface Step1Props {
  onContinue: () => void;
  selectedProduct: StripeProduct | null;
}

const Step1 = ({ onContinue, selectedProduct }: Step1Props) => {
  const [activeClinicId, setActiveClinicId] = useState<number | null>(null);
  const [showClinicDetails, setShowClinicDetails] = useState<boolean>(false);
  const [selectedClinicForDetails, setSelectedClinicForDetails] = useState<ScanCenter | null>(null);
  const [clinics, setClinics] = useState<ScanCenter[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [hasSearched, setHasSearched] = useState<boolean>(false);
  const [searchQuery, setSearchQuery] = useState<string>('');

  const MIN_POSTCODE_CHARS = 3;

  let restoredPostcode = '';
  try {
    restoredPostcode = sessionStorage.getItem('scanCheckoutLastPostcode') || '';
  } catch {}

  let bookingData: any = null;
  try {
    const bookingDataStr = localStorage.getItem('bookingData');
    bookingData = bookingDataStr ? JSON.parse(bookingDataStr) : null;
  } catch {}

  const bookingSiteId: number | null =
    typeof bookingData?.site_id === 'number' ? bookingData.site_id : null;
  const bookingDesireDate: string | null = bookingData?.desire_date ?? null;

  const sortClinics = useCallback((items: ScanCenter[]) => {
    return [...items].sort((a, b) => {
      // Recommended first.
      if (a.recommended !== b.recommended) return a.recommended ? -1 : 1;
      // Then shortest distance.
      return (a.distance ?? Number.POSITIVE_INFINITY) - (b.distance ?? Number.POSITIVE_INFINITY);
    });
  }, []);

  const handleCardClick = (clinicId: number) => {
    setActiveClinicId(clinicId);
  };

  const handleSearch = useCallback(async (location: string) => {
    const trimmed = location.trim();
    if (!trimmed || trimmed.length < MIN_POSTCODE_CHARS) {
      setClinics([]);
      setHasSearched(false);
      setActiveClinicId(null);
      return;
    }

    try {
      sessionStorage.setItem('scanCheckoutLastPostcode', trimmed);
    } catch {}

    setHasSearched(true);
    setIsLoading(true);
    try {
      const response = await searchScanCenters({ location: trimmed });
      const nextClinics = sortClinics(response.data ?? []);

      // Cache results for back-navigation/refresh (avoid re-calling search).
      try {
        sessionStorage.setItem(
          'scanCheckoutClinicsCache',
          JSON.stringify({ postcode: trimmed, clinics: nextClinics })
        );
      } catch {}

      if (nextClinics.length > 0) {
        setClinics(nextClinics);
        // Auto-expand the first result (map + date request).
        setActiveClinicId(nextClinics[0].id);
      } else {
        setClinics([]);
        setActiveClinicId(null);
        toast.info('No scan centres found for this location. Try a different postcode.');
      }
    } catch (err: any) {
      console.error('Error fetching scan centres:', err);
      toast.error('Failed to load scan centres. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, [sortClinics]);

  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const debouncedSearch = useCallback((query?: string) => {
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }
    debounceTimer.current = setTimeout(() => {
      const value = (query ?? '').trim();
      setActiveClinicId(null);
      handleSearch(value);
    }, 500);
  }, [handleSearch]);

  useEffect(() => {
    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
    };
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchQuery(value);
    debouncedSearch(value);
  };

  // Restore from cached search results (avoid loading extra API calls).
  useEffect(() => {
    if (!restoredPostcode) return;
    setSearchQuery(restoredPostcode);

    try {
      const raw = sessionStorage.getItem('scanCheckoutClinicsCache');
      if (!raw) return;
      const parsed = JSON.parse(raw) as { postcode?: string; clinics?: ScanCenter[] };
      const cachedPostcode = parsed?.postcode;
      const cachedClinics = parsed?.clinics;

      if (cachedPostcode !== restoredPostcode) return;
      if (!Array.isArray(cachedClinics)) return;

      const sorted = sortClinics(cachedClinics);
      setClinics(sorted);
      setHasSearched(true);

      if (bookingSiteId != null) {
        const exists = sorted.some((c) => c.id === bookingSiteId);
        setActiveClinicId(exists ? bookingSiteId : null);
      } else if (sorted.length > 0) {
        setActiveClinicId(sorted[0].id);
      }
    } catch {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const closestClinicId = useMemo(() => {
    if (clinics.length === 0) return null;
    let closest: ScanCenter | null = null;
    for (const c of clinics) {
      const d = c.distance ?? Number.POSITIVE_INFINITY;
      if (closest == null || d < (closest.distance ?? Number.POSITIVE_INFINITY)) {
        closest = c;
      }
    }
    return closest?.id ?? null;
  }, [clinics]);

  const handleContinueToPersonalDetails = (
    selectedDate: string,
    siteId: number,
    siteName: string,
    postcode: string,
    centerAddress: string
  ) => {
    const locationName = (siteName || '').trim() || null;
    const bookingData = {
      desire_date: selectedDate,
      site_id: siteId,
      site_name: locationName,
      location: locationName,
      postcode: (postcode || '').trim() || null,
      center_address: (centerAddress || '').trim() || null,
    };
    localStorage.setItem('bookingData', JSON.stringify(bookingData));

    if (selectedProduct) {
      const selectedPlanData = {
        type: 'fullBody' as const,
        testCount: null,
        product: selectedProduct,
        productId: selectedProduct.stripe_price_yearly_id || selectedProduct.stripe_price_monthly_id,
        paymentType: 'yearly' as const,
      };
      localStorage.setItem('selectedPlan', JSON.stringify(selectedPlanData));
      localStorage.setItem('subscriptionAmount', (selectedProduct.yearly_amount || 1650).toString());
    }

    onContinue();
  };

  const handleClinicDetailsClick = (clinic: ScanCenter) => {
    setSelectedClinicForDetails(clinic);
    setShowClinicDetails(true);
  };

  return (
    <div className='inner-container2' >
        <StepHeader 
        stepCount="Step 1 of 3"
        title="Scan centre and date selection"
        stepProgress={33.33}
        />
        <div className='find-centre-section'  >
        <h2 className='section-title' >Find your nearest scan centre</h2>
        <div className='postcode-input-wrapper' >
            <input
              type="text"
              placeholder='Enter postcode'
              className='input-field'
              value={searchQuery}
              onChange={handleInputChange}
            />
        </div>
        {!isLoading && !hasSearched && (
          <div className='no-results-found'>
            <img src={LocationIcon} alt="No results found" />
            <div>Scan centres will display once you’ve entered your postcode.</div>
          </div>
        )}

        {isLoading && (
          <div style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}>
            <p>Loading scan centres...</p>
          </div>
        )}

        {!isLoading && clinics.length > 0 && (
          <div className='scan-centres-list' >
            {clinics.map((clinic) => (
              <ScanCentreCard
                key={clinic.id}
                clinic={clinic}
                isClosest={closestClinicId != null && clinic.id === closestClinicId}
                isFavourite={clinic.recommended}
                isActive={activeClinicId === clinic.id}
                searchPostcode={searchQuery}
                initialSelectedDate={
                  bookingSiteId === clinic.id ? (bookingDesireDate ?? undefined) : undefined
                }
                onCardClick={() => handleCardClick(clinic.id)}
                handleClinicDetailsClick={() => handleClinicDetailsClick(clinic)}
                handleContinueToPersonalDetailsClick={handleContinueToPersonalDetails}
              />
            ))}
          </div>
        )}

        {!isLoading && hasSearched && clinics.length === 0 && (
          <div style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}>
            <p>No scan centres found. Try a different postcode.</p>
          </div>
        )}
        </div>

        {showClinicDetails && (
          <ClinicDetailsModal
            clinic={selectedClinicForDetails}
            handleCloseClinicDetails={() => setShowClinicDetails(false)}
          />
        )}
    </div>
  )
}

export default Step1;
